import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbBigDecimal;
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { NumberUtils } from "abweb/utils/NumberUtils";
import { Assert } from "abweb/utils/Assert";
import { LogUtils } from "./log/LogUtils";

/**
 * Utility class for storing a lossless decimal. Can do most necessary operations except for division (because that is much harder to do well).
 * Stores the value in scientific notation of sorts--
 * value = sigFigs * 10 ^ exponent
 */
// AbBigDecimal.POSITIVE_INFINITY = 1 * 10 ^ inf
// AbBigDecimal.NEGATIVE_INFINITY = -1 * 10 ^ inf
// AbBigDecimal.NaN = 1 * 10 ^ NaN

const LOGGER = LogUtils.getLogger(__filename);
var _stringCache = /*#__PURE__*/new WeakMap();
var _numberCache = /*#__PURE__*/new WeakMap();
var _calcString = /*#__PURE__*/new WeakMap();
export class AbBigDecimal {
  constructor(sigFigs, exponent) {
    _defineProperty(this, "sigFigs", void 0);
    _defineProperty(this, "exponent", void 0);
    _classPrivateFieldInitSpec(this, _stringCache, void 0);
    _classPrivateFieldInitSpec(this, _numberCache, void 0);
    /**
     * Adds this decimal to another losslessly and returns the result.
     */
    _defineProperty(this, "isFinite", () => {
      return AbBigDecimal.isFinite(this);
    });
    /**
     * Adds this decimal to another losslessly and returns the result.
     */
    _defineProperty(this, "isNaN", () => {
      return AbBigDecimal.isNaN(this);
    });
    /**
     * Adds this decimal to another losslessly and returns the result.
     */
    _defineProperty(this, "add", other => {
      return AbBigDecimal.add(this, other);
    });
    /**
     * Multiplies this decimal with another losslessly and returns the result.
     */
    _defineProperty(this, "multiply", other => {
      return AbBigDecimal.multiply(this, other);
    });
    /**
     * Divides this decimal with powers of 2 or 5 losslessly and returns the result.
     */
    _defineProperty(this, "divide", (powerOf2, powerOf5) => {
      return AbBigDecimal.divide(this, powerOf2, powerOf5);
    });
    /**
     * Compares this decimal to another.
     */
    _defineProperty(this, "compare", other => {
      return AbBigDecimal.compare(this, other);
    });
    /**
     * Returns true if decimals are equal.
     */
    _defineProperty(this, "equals", other => {
      return AbBigDecimal.equals(this, other);
    });
    /**
     * Truncates the number of significant figures after the decimal point.
     */
    _defineProperty(this, "truncate", numDecimalPlaces => {
      return AbBigDecimal.truncate(this, numDecimalPlaces);
    });
    _classPrivateFieldInitSpec(this, _calcString, () => {
      const numberString = this.sigFigs.toString();
      if (this.exponent === Number.POSITIVE_INFINITY && this.sigFigs < 0) return Number.NEGATIVE_INFINITY.toString();
      if (this.exponent === Number.POSITIVE_INFINITY && this.sigFigs > 0) return Number.POSITIVE_INFINITY.toString();
      if (isNaN(this.exponent)) return this.exponent.toString();
      if (this.exponent === 0) return numberString;
      if (this.exponent > 0) return numberString + "0".repeat(this.exponent);
      if (-this.exponent >= numberString.length) return `0.${"0".repeat(-this.exponent - numberString.length)}${numberString}`;
      const decimalDividerIndex = numberString.length + this.exponent;
      return `${numberString.substring(0, decimalDividerIndex)}.${numberString.substring(decimalDividerIndex)}`;
    });
    /**
     * Returns a "." separated decimal representation of the number.
     */
    _defineProperty(this, "toString", () => {
      if (_classPrivateFieldGet(_stringCache, this) === undefined) _classPrivateFieldSet(_stringCache, this, _classPrivateFieldGet(_calcString, this).call(this));
      return _classPrivateFieldGet(_stringCache, this);
    });
    /**
     * Localizes the decimal separator.
     */
    _defineProperty(this, "toLocaleString", () => {
      if (this.isNaN()) return this.exponent.toLocaleString();
      if (!isFinite(this.exponent)) return `${this.sigFigs > 0 ? "" : "-"}${this.exponent.toLocaleString()}`;
      return this.toString().replace(".", NumberUtils.getLocaleDecimalSeparator());
    });
    /**
     * Returns a "." separated decimal representation of the number.
     */
    _defineProperty(this, "toIntlNumberFormatterInput", () => {
      if (this.isNaN()) return Number.NaN;
      return this.toString();
    });
    // Logic strips off trailing zeros of sigFigs
    const sigFigString = sigFigs.toString();
    const numZeros = sigFigString.split("").reverse().findIndex(c => c !== "0");
    if (numZeros === -1) {
      this.sigFigs = 0n;
      this.exponent = 0;
    } else {
      this.sigFigs = sigFigs / 10n ** BigInt(numZeros);
      this.exponent = exponent + numZeros;
    }
  }

  /**
   * Converts the stored value into a JS number. USE SPARINGLY as JS numbers are floats and thus lose precision.
   */
  get numberValue() {
    if (_classPrivateFieldGet(_numberCache, this) === undefined) _classPrivateFieldSet(_numberCache, this, Number(this.toString()));
    return _classPrivateFieldGet(_numberCache, this);
  }
  /**
   * Constructs an AbBigDecimal. Either constructs from a string (can be localized), a number,
   * or the underlying sigFigs and exponent.
   */
  static from(sigFigsOrRaw, exponent) {
    if (typeof sigFigsOrRaw === "bigint") {
      if (exponent === Number.POSITIVE_INFINITY && sigFigsOrRaw > 0) return AbBigDecimal.POSITIVE_INFINITY;
      if (exponent === Number.POSITIVE_INFINITY && sigFigsOrRaw < 0) return AbBigDecimal.NEGATIVE_INFINITY;
      if (exponent === Number.POSITIVE_INFINITY && sigFigsOrRaw === 0n) return new AbBigDecimal(0n, 1);
      if (exponent !== undefined && isNaN(exponent)) return AbBigDecimal.NaN;
      if (exponent !== undefined && !Number.isInteger(exponent)) {
        LOGGER.error(`Exponent must be a whole number. '${exponent}' is not a whole number.`);
        return AbBigDecimal.NaN;
      }
      return new AbBigDecimal(sigFigsOrRaw, exponent ?? 0);
    } else if (typeof sigFigsOrRaw === "string") {
      if (sigFigsOrRaw === Number.POSITIVE_INFINITY.toString()) return AbBigDecimal.POSITIVE_INFINITY;
      if (sigFigsOrRaw === Number.NEGATIVE_INFINITY.toString()) return AbBigDecimal.NEGATIVE_INFINITY;
      if (sigFigsOrRaw === Number.NaN.toString()) return AbBigDecimal.NaN;
      // find decimal point if present
      const sep = NumberUtils.getLocaleDecimalSeparator();
      if (sep !== ".") {
        // We have to convert sep's (most likely ",") to "."
        sigFigsOrRaw = sigFigsOrRaw.replace(sep, ".");
      }
      if (!AbBigDecimal.isValidString(sigFigsOrRaw)) {
        LOGGER.error(`'${sigFigsOrRaw}' is not a valid decimal representation`);
        return AbBigDecimal.NaN;
      }
      const exponentToks = sigFigsOrRaw.split(new RegExp(/[eE]/));
      Assert.assert(exponentToks.length === 1 || exponentToks.length === 2); // Don't need to do LOGGER error or NaN, just a sanity check
      const sigFigs = BigInt(exponentToks[0].replace(".", ""));
      const additionalExponent = exponentToks.length === 1 ? 0 : Number(exponentToks[1]);
      const decimalDividerIndex = exponentToks[0].indexOf(".");
      if (decimalDividerIndex === -1) {
        return new AbBigDecimal(sigFigs, additionalExponent);
      }
      return new AbBigDecimal(sigFigs, decimalDividerIndex - exponentToks[0].length + 1 + additionalExponent);
    } else {
      return AbBigDecimal.from(sigFigsOrRaw.toString());
    }
  }
}
_AbBigDecimal = AbBigDecimal;
_defineProperty(AbBigDecimal, "POSITIVE_INFINITY", new _AbBigDecimal(1n, Number.POSITIVE_INFINITY));
_defineProperty(AbBigDecimal, "NEGATIVE_INFINITY", new _AbBigDecimal(-1n, Number.POSITIVE_INFINITY));
_defineProperty(AbBigDecimal, "NaN", new _AbBigDecimal(1n, Number.NaN));
_defineProperty(AbBigDecimal, "isFinite", bigDecimal => {
  return Number.isFinite(bigDecimal.exponent);
});
_defineProperty(AbBigDecimal, "isNaN", bigDecimal => {
  return Number.isNaN(bigDecimal.exponent);
});
/**
 * Adds two bigdecimals losslessly and returns the result.
 */
_defineProperty(AbBigDecimal, "add", (decimal1, decimal2) => {
  if (decimal1.isNaN() || decimal2.isNaN()) return _AbBigDecimal.NaN;
  if (decimal1.isFinite() && decimal2.isFinite()) {
    if (decimal1.exponent === decimal2.exponent) {
      return new _AbBigDecimal(decimal1.sigFigs + decimal2.sigFigs, decimal1.exponent);
    } else if (decimal1.exponent < decimal2.exponent) {
      const deltaExponent = decimal2.exponent - decimal1.exponent;
      return new _AbBigDecimal(decimal1.sigFigs + decimal2.sigFigs * 10n ** BigInt(deltaExponent), decimal1.exponent);
    } else {
      return _AbBigDecimal.add(decimal2, decimal1);
    }
  } else {
    if (decimal2.isFinite()) return new _AbBigDecimal(decimal1.sigFigs, decimal1.exponent);
    if (decimal1.isFinite()) return new _AbBigDecimal(decimal2.sigFigs, decimal2.exponent);
    if (decimal1.sigFigs === decimal2.sigFigs) return new _AbBigDecimal(decimal1.sigFigs, decimal1.exponent);
    return _AbBigDecimal.NaN;
  }
});
/**
 * Multiplies two bigdecimals losslessly and returns the result.
 */
_defineProperty(AbBigDecimal, "multiply", (decimal1, maybeDecimal2) => {
  const decimal2 = _assertClassBrand(_AbBigDecimal, _AbBigDecimal, _convert)._.call(_AbBigDecimal, maybeDecimal2);
  if (!_AbBigDecimal.isFinite(decimal1) && decimal2.sigFigs === 0n || !_AbBigDecimal.isFinite(decimal2) && decimal1.sigFigs === 0n) return _AbBigDecimal.NaN;
  return new _AbBigDecimal(decimal1.sigFigs * decimal2.sigFigs, decimal1.exponent + decimal2.exponent);
});
/**
 * Divides a big decimal by powers of 2 and 5. Other values are not supported. 
 */
_defineProperty(AbBigDecimal, "divide", (decimal, powerOf2, powerOf5) => {
  if (!Number.isInteger(powerOf2)) throw new Error(`Power of 2 '${powerOf2}' must be an integer.`);
  if (!Number.isInteger(powerOf5)) throw new Error(`Power of 2 '${powerOf5}' must be an integer.`);
  return _AbBigDecimal.multiply(decimal, 5 ** powerOf2).multiply(2 ** powerOf5).multiply(new _AbBigDecimal(1n, -1 * (powerOf2 + powerOf5)));
});
var _compareBigInt = {
  _: (v1, v2) => {
    if (v1 < v2) return -1;
    if (v1 > v2) return 1;
    return 0;
  }
};
var _convert = {
  _: maybeDecimal2 => {
    if (typeof maybeDecimal2 === "bigint") return new _AbBigDecimal(maybeDecimal2, 0);
    if (typeof maybeDecimal2 === "number") return _AbBigDecimal.from(maybeDecimal2);
    return maybeDecimal2;
  }
};
/**
 * Compares two bigdecimals and returns the result.
 * decimal1 > decimal2 => 1
 * decimal1 < decimal2 => -1
 * decimal1 = decimal2 => 0
 */
_defineProperty(AbBigDecimal, "compare", (decimal1, maybeDecimal2) => {
  const decimal2 = _assertClassBrand(_AbBigDecimal, _AbBigDecimal, _convert)._.call(_AbBigDecimal, maybeDecimal2);
  if (decimal1.isNaN()) return -1;
  if (decimal2.isNaN()) return 1;
  if (decimal1.exponent === decimal2.exponent) {
    return _compareBigInt._.call(_AbBigDecimal, decimal1.sigFigs, decimal2.sigFigs);
  } else if (decimal1.exponent < decimal2.exponent) {
    const deltaExponent = decimal2.exponent - decimal1.exponent;
    if (!isFinite(deltaExponent)) return decimal2.sigFigs > 0 ? -1 : 1;
    return _compareBigInt._.call(_AbBigDecimal, decimal1.sigFigs, decimal2.sigFigs * 10n ** BigInt(deltaExponent));
  } else {
    const deltaExponent = decimal1.exponent - decimal2.exponent;
    if (!isFinite(deltaExponent)) return decimal1.sigFigs > 0 ? 1 : -1;
    return _compareBigInt._.call(_AbBigDecimal, decimal1.sigFigs * 10n ** BigInt(deltaExponent), decimal2.sigFigs);
  }
});
/**
 * Returns true if decimals are equal.
 */
_defineProperty(AbBigDecimal, "equals", (decimal1, decimal2) => {
  return _AbBigDecimal.compare(decimal1, decimal2) === 0;
});
/**
 * Truncates the number of significant figures after the decimal point. numDecimalPlaces must be non-negative and integral.
 */
_defineProperty(AbBigDecimal, "truncate", (decimal, numDecimalPlaces) => {
  Assert.assert(!isNaN(numDecimalPlaces), "invalid numDecimalPlaces passed to truncate()");
  Assert.assert(numDecimalPlaces >= 0, "truncate() on a number requires a nonnegative integer value for numDecimalPlaces.");
  Assert.assert(Number.isInteger(numDecimalPlaces), "truncate() on a number requires a nonnegative integer value for numDecimalPlaces.");
  if (!_AbBigDecimal.isFinite(decimal)) return decimal; // Infinities or NaN aren't truncated
  // Just return if there are fewer than that many digits after the decimal point
  const deltaNumDecimalPlaces = -decimal.exponent - numDecimalPlaces;
  if (deltaNumDecimalPlaces <= 0) return decimal;
  // Bigint division floors the result. So remove some significant figures and move the exponent.
  const sigFigs = decimal.sigFigs / 10n ** BigInt(deltaNumDecimalPlaces);
  const exponent = decimal.exponent + deltaNumDecimalPlaces;
  return new _AbBigDecimal(sigFigs, exponent);
});
var _validRegex = {
  _: new RegExp(/^([-+]?((\d+\.?\d*)|(\d*\.?\d+))([eE][-+]?\d+)?)?$/)
};
/**
 * Calculates whether the given string (can be localized) can be parsed into an AbBigDecimal.
 */
_defineProperty(AbBigDecimal, "isValidString", s => {
  if (["Infinity", "-Infinity", "+Infinity", "NaN"].includes(s)) return true;
  return _validRegex._.test(s.replace(NumberUtils.getLocaleDecimalSeparator(), "."));
});
//# sourceMappingURL=AbBigDecimal.js.map
